import React, { lazy, Suspense } from "react";
import { Await } from "react-router";
import CustomError from "@components/Error/CustomError";
import Spinner from "@components/Spinner/Spinner";
import ArticleWrapper from "@pages/Article/ArticleWrapper";
import { ArticlePageContext } from "@pages/Article/utils/types";
import { TRouteWithRedirect, useRouteContext } from "@sphtech/web2-core/ssr";
import { HttpStatusCode } from "axios";

import { StoryThreadContext } from "./StoryThread/utilities/types";

const StoryThreadPage = lazy(
  () => import("@pages/StoryThread/StoryThreadPage")
);

const DefaultPageHandler = (): React.ReactElement => {
  const { context } =
    useRouteContext<
      TRouteWithRedirect<ArticlePageContext | StoryThreadContext, string>
    >();

  // If context is missing entirely
  if (!context) {
    return <CustomError />;
  }

  // StatusCode is not 200
  if (context.statusCode !== HttpStatusCode.Ok) {
    return <CustomError statusCode={context.statusCode} />;
  }

  // If payload is missing
  if (typeof context?.payload === "undefined") {
    return <CustomError />;
  }

  return (
    <Await resolve={context.payload} errorElement={<CustomError />}>
      {(context: ArticlePageContext | StoryThreadContext) => {
        const { kind, data } = context;

        // Load article page content.
        if (kind === "art") {
          return <ArticleWrapper data={data} />;
        }

        // Load storythread page content.
        if (kind === "storythread") {
          const { entity } = context;

          return (
            <Suspense fallback={<Spinner />}>
              <StoryThreadPage {...{ data, entity }} />
            </Suspense>
          );
        }

        // Default error page.
        return <CustomError statusCode={HttpStatusCode.NotFound} />;
      }}
    </Await>
  );
};

export default DefaultPageHandler;
